/**
 * t711u: "Find your address" above the HR home address. UK only, through the API's Google Places proxy (the key stays
 * on the server), one session token per search. Picking fills line 1, line 2, town, county and postcode; every field
 * stays editable. With no Google key set up, or if a lookup fails, the box goes away / says so and the address is typed in.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Search } from 'lucide-react';
import { newPlacesSession, type AddressParts } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { Field, Input } from '@/components/ui';

export function AddressLookup({ onPick }: { onPick: (a: AddressParts) => void }) {
  const ws = useWorkspaceId();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [off, setOff] = useState(false);
  const session = useRef(newPlacesSession());
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  // Is lookup set up for this workspace? (No Places call: the box only shows when it can work.)
  const probe = useQuery({ queryKey: ['hr-address-lookup', ws], enabled: Boolean(ws), staleTime: 10 * 60_000, retry: false,
    queryFn: () => api.hr.addressLookup(ws!).then(r => r.available).catch(() => false) });
  const r = useQuery({ queryKey: ['hr-address', ws, debounced], enabled: Boolean(ws && debounced.length >= 3 && probe.data), staleTime: 60_000, retry: false,
    queryFn: () => api.hr.addressSearch(ws!, debounced, session.current) });
  useEffect(() => { if (r.data && !r.data.available) setOff(true); }, [r.data]);
  if (off || !probe.data) return null;
  const pick = async (placeId: string) => {
    setErr(null);
    try {
      const a = await api.hr.addressPick(ws!, placeId, session.current);
      onPick(a); setQ(''); setOpen(false);
    } catch (e) { setErr(`${errorMessage(e)}. Type the address in below.`); }
    finally { session.current = newPlacesSession(); }
  };
  const list = open && debounced.length >= 3 ? r.data?.suggestions ?? [] : [];
  return (
    <Field label="Find your address" className="sm:col-span-2" hint={<span className="text-xs text-slate-500">Start typing a UK address or postcode, or fill in the boxes below</span>}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
        <Input className="pl-8" value={q} placeholder="e.g. 12 High Street or LL11 1AA" autoComplete="off" role="combobox" aria-expanded={list.length > 0} aria-autocomplete="list"
          onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }} data-testid="hr-address-search" />
        {list.length > 0 && (
          <div role="listbox" className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg dark:border-white/10 dark:bg-slate-900" data-testid="hr-address-results">
            {list.map(s => (
              <button key={s.place_id} type="button" role="option" aria-selected={false} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-white/5"
                onClick={() => void pick(s.place_id)} data-testid="hr-address-option">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden /><span className="min-w-0 truncate">{s.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {r.isError && !err && <p className="mt-1 text-xs text-slate-500" data-testid="hr-address-error">Address search isn’t working right now. Type the address in below.</p>}
      {err && <p className="mt-1 text-xs text-rose-700" role="alert" data-testid="hr-address-error">{err}</p>}
    </Field>
  );
}
